import DataQuery from 'data/DataQuery';
import { Customer_Contacts } from 'data/endpoints';
import IRepository from 'data/IRepository';
import PostResponse from 'data/PostResponse';
import ContactDTO from 'features/customer/dtos/ContactDTO';
import InsertRequirementResultDTO from 'features/requirements/dtos/InsertRequirementResultDTO';
import { openRequirementResultDialog } from 'features/requirements/helpers/requirementResultDialogHelper';
import BaseRequirementFormProps from 'features/requirements/models/BaseRequirementFormProps';
import { RequirementResultDialogParams } from 'features/requirements/models/RequirementResultDialogParams';
import IRequirementCreateService from 'features/requirements/services/IRequirementCreateService';
import PageCard from 'features/shared/components/card/PageCard';
import RenderWrapper from 'features/shared/components/renderWrapper/RenderWrapper';
import FormGroup from 'features/shared/forms/components/FormGroup';
import { useForm } from 'features/shared/forms/hooks/useForm';
import { getCustomerContactsParams } from 'features/shared/helpers/queryParams';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import CustomerState from 'infrastructure/store/customer/CustomerState';
import IAppState from 'infrastructure/store/IAppState';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import BasicInfoSubFormSection from 'features/requirements/sections/requirementFormTypes/basicInfoSubForm/BasicInfoSubFormSection';
import { phoneFormValidations } from './phoneFormValidation';

interface Props extends BaseRequirementFormProps {
}

const PhoneFormSection = (props?: Props) => {
  //translations
  const tLabels = useTranslation("requirements\\requirements").t;

  // DI
  const createService = useInjection<IRequirementCreateService>(IocTypes.RequirementCreateService);
  const repository = useInjection<IRepository<ContactDTO>>(IocTypes.Repository);

  // router location
  const location = useLocation<ContactDTO>();

  // props
  const useOutsideOfRequirements = props?.useOutsideOfRequirements ? true : false;
  const sendButtonTitle = props?.sendButtonTitle ? props.sendButtonTitle : tLabels("phoneFormSection.sendRequirement");
  const successMessage: string = useOutsideOfRequirements ? tLabels("phoneFormSection.successMessageOutsideRequirement") : tLabels("phoneFormSection.successMessage");
  const errorMessage: string | undefined = useOutsideOfRequirements ? tLabels("phoneFormSection.errorMessage") : undefined;

  // store
  const customer = (useSelector<IAppState, CustomerState>((store) => store.customer)).customer;

  // form hook
  const { values, setFormValue, errors, bindField, bindUncontrolledField, valideteForm, setValidationRule } = useForm({
    initialValidations: phoneFormValidations()
  });

  // side effects
  useEffect(() => {
    setOriginalPhone();
  }, [])

  // helpers 
  const setOriginalPhone = async () => {
    // load contact from location (router)
    if (location.state) {
      setFormValue("DESC_TMTELEFONPUVODNI", location.state.value); 
    }
    // load contact from API
    else {
      let query: DataQuery = {url: Customer_Contacts, routeParams: getRouteParams("customerId", customer.customerId!), params: getCustomerContactsParams("Phone", 1)};
      let contacts = await repository.getPagedResult(query);
      if (contacts.count === 1) {
        setFormValue("DESC_TMTELEFONPUVODNI", contacts.result[0].value); 
      }
    }
  }

  // handlers
  const submitFormHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (valideteForm()) {
      let valuesCopy = {...values};
      valuesCopy["DESC_TMTELEFON"] = values["DESC_TMTELEFON"].replace(/\s/g, "");
      let result = await createService.createRequirement("ZMENATELEFONU", valuesCopy);
      openResultDialogHandler(result);
    }
  };

  const openResultDialogHandler = (result: PostResponse<InsertRequirementResultDTO>) => {
    const params: RequirementResultDialogParams = {
      response: result,
      successMessage: successMessage,
      errorMessage: errorMessage,
      actionBtn: { title: tLabels("phoneFormSection.identificationData"), route: "/customer" },
      transitonBtn: null
    };

    openRequirementResultDialog(useOutsideOfRequirements, params, tLabels("phoneFormSection.changeMobileNumber"), true); 
  }

  // render
  return (
    <form onSubmit={submitFormHandler}>
      <PageCard
        title={tLabels("phoneFormSection.changeMobileNumber")}
        bodyClassName="pt-4 px-4"
      >
        <div className="row no-gutters mx-n2">
          <div className="col-sm-6 px-2">
            <FormGroup
              labelText={tLabels("phoneFormSection.originalMobileNumber")}
            >
              <input
                className="form-control"
                disabled
                defaultValue={values["DESC_TMTELEFONPUVODNI"] ?? ""}
              />
            </FormGroup>
          </div>
          <div className="col-sm-6 px-2">
            <FormGroup
              labelText={tLabels("phoneFormSection.newMobileNumber")}
              inputId="DESC_TMTELEFON"
              validationError={errors["DESC_TMTELEFON"]}
            >
              <input
                className="form-control"
                {...bindField("DESC_TMTELEFON")}
              />
            </FormGroup>
          </div>
        </div>
      </PageCard>

      <RenderWrapper isVisible={() => !useOutsideOfRequirements}>
        <BasicInfoSubFormSection
          bindField={bindField}
          bindUncontrolledField={bindUncontrolledField}
          errors={errors}
          setValidationRule={setValidationRule}
        />
      </RenderWrapper>

      <button type="submit" className="btn btn-primary px-3 py-2">
        {sendButtonTitle}
      </button>
    </form>
  )
}

export default PhoneFormSection;